import { useEffect, useMemo, useState } from "react";
import clsx from "clsx";
import { useQuery } from "@tanstack/react-query";
import {
  PARAMETERS,
  PARAMETER_LIST,
  type ParameterId,
} from "@tbm/shared";
import { api } from "../lib/api";
import { trackFeature } from "../lib/analytics";
import { usePreprocessingParams } from "../state/preprocessing";
import { localNaiveInputToUtcIso, useTimezone } from "../lib/datetime";

interface Props {
  open: boolean;
  onClose: () => void;
}

type Mode = "raw" | "processed";

// Skip the "jacks" group (active station + per-station ram displacement).
// Those are dev-only plotting helpers and aren't useful as user downloads.
const ALL_IDS: ParameterId[] = PARAMETER_LIST
  .filter((p) => p.group !== "jacks")
  .map((p) => p.id);

/**
 * Format a UTC date as a "YYYY-MM-DDTHH:MM" naive string in the given
 * IANA timezone — for prefilling <input type="datetime-local"> with the
 * user's local wall-clock. Browser-local fallback (`d.getHours()` etc.)
 * is intentionally avoided here because the user's machine TZ may not
 * match the drive's configured timezone.
 */
export function toLocalInputInTz(d: Date, tz: string): string {
  const dtf = new Intl.DateTimeFormat("en-CA", {
    timeZone: tz,
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
    hour12: false,
  });
  const parts = dtf.formatToParts(d);
  const m: Record<string, string> = {};
  for (const p of parts) m[p.type] = p.value;
  let hour = m.hour ?? "00";
  if (hour === "24") hour = "00";
  return `${m.year}-${m.month}-${m.day}T${hour}:${m.minute ?? "00"}`;
}

export function DownloadDrawer({ open, onClose }: Props) {
  const drive = useQuery({ queryKey: ["drive"], queryFn: api.drive });
  const pp = usePreprocessingParams();
  const tz = useTimezone();

  const [mode, setMode] = useState<Mode>("processed");
  const [selected, setSelected] = useState<Set<ParameterId>>(new Set(ALL_IDS));
  const [fromInput, setFromInput] = useState<string>("");
  const [toInput, setToInput] = useState<string>("");
  const [downloading, setDownloading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (drive.data?.startDate && fromInput === "") {
      setFromInput(toLocalInputInTz(new Date(drive.data.startDate), tz));
    }
  }, [drive.data?.startDate, fromInput, tz]);
  useEffect(() => {
    if (toInput === "") setToInput(toLocalInputInTz(new Date(), tz));
  }, [toInput, tz]);

  // Slurry monitoring PC data (own table, own timestamps) — separate CSV.
  const slurry = useQuery({
    queryKey: ["slurry-summary"],
    queryFn: api.slurrySummary,
    enabled: open,
    refetchInterval: open ? 60_000 : false,
  });
  const slurryCount = (slurry.data?.variables ?? []).reduce((n, v) => n + v.count, 0);
  const slurryLast = (slurry.data?.variables ?? []).reduce<string | null>(
    (m, v) => (m == null || v.lastTs > m ? v.lastTs : m),
    null,
  );
  const slurryHref =
    fromInput && toInput
      ? api.slurryExportUrl({
          from: localNaiveInputToUtcIso(fromInput, tz),
          to: localNaiveInputToUtcIso(toInput, tz),
        })
      : null;

  const ids = useMemo(() => ALL_IDS.filter((id) => selected.has(id)), [selected]);
  const canDownload = ids.length > 0 && !!fromInput && !!toInput && !downloading;

  function toggle(id: ParameterId) {
    setSelected((cur) => {
      const next = new Set(cur);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }
  function selectAll() {
    setSelected(new Set(ALL_IDS));
  }
  function selectNone() {
    setSelected(new Set());
  }

  async function handleDownload() {
    if (!canDownload) return;
    setError(null);
    setDownloading(true);
    trackFeature(`csv_export_${mode}`);
    try {
      // Parse the user's wall-clock input AS the configured timezone,
      // independent of whatever TZ the browser thinks it is.
      const fromIso = localNaiveInputToUtcIso(fromInput, tz);
      const toIso = localNaiveInputToUtcIso(toInput, tz);
      const url = api.exportUrl({
        ids,
        from: fromIso,
        to: toIso,
        mode,
        preprocessing: pp,
      });
      const res = await fetch(url);
      if (!res.ok) throw new Error(`Export failed: ${res.status} ${res.statusText}`);
      const blob = await res.blob();
      const stamp = new Date().toISOString().replace(/[:.]/g, "-");
      const filename = `tbm_export_${mode}_${stamp}.csv`;
      const objectUrl = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = objectUrl;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      a.remove();
      setTimeout(() => URL.revokeObjectURL(objectUrl), 1500);
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setDownloading(false);
    }
  }

  return (
    <>
      <div
        className={clsx(
          "fixed inset-0 bg-black/25 transition-opacity z-40",
          open ? "opacity-100" : "opacity-0 pointer-events-none",
        )}
        onClick={onClose}
      />
      <aside
        className={clsx(
          "fixed top-0 right-0 bottom-0 w-[440px] max-lg:w-full max-lg:max-w-none z-50 bg-surface1 border-l border-border1",
          "transform transition-transform duration-200",
          open ? "translate-x-0" : "translate-x-full",
        )}
      >
        <div className="flex items-center justify-between h-16 px-6 border-b border-border1">
          <div>
            <div className="text-[10.5px] uppercase tracking-[0.22em] text-dim">Tools</div>
            <h2 className="text-[15px] font-semibold text-text">Download data</h2>
          </div>
          <button onClick={onClose} className="text-muted hover:text-text text-lg">×</button>
        </div>

        <div className="p-6 space-y-6 overflow-y-auto h-[calc(100%-128px)]">
          <Section
            title="Mode"
            subtitle={
              mode === "raw"
                ? "Unprocessed TBM data."
                : "Processed TBM data based on current preprocessing settings."
            }
          >
            <div className="grid grid-cols-2 bg-surface2 border border-border1 rounded overflow-hidden">
              {(["raw", "processed"] as Mode[]).map((m) => (
                <button
                  key={m}
                  onClick={() => setMode(m)}
                  className={clsx(
                    "py-1.5 text-[12px] tracking-wider uppercase transition-colors",
                    mode === m ? "bg-wb text-white" : "text-muted hover:bg-surface3 hover:text-text",
                  )}
                >
                  {m === "raw" ? "Raw" : "Processed"}
                </button>
              ))}
            </div>
          </Section>

          <Section title="Time range">
            <DateField label="From" value={fromInput} onChange={setFromInput} />
            <DateField label="To"   value={toInput}   onChange={setToInput} />
          </Section>

          <Section title="Parameters">
            <div className="flex gap-2 mb-2">
              <button
                onClick={selectAll}
                className="text-[10.5px] tracking-wider uppercase text-muted hover:text-text border border-border2 hover:border-wb rounded px-2 py-1"
              >
                Select all
              </button>
              <button
                onClick={selectNone}
                className="text-[10.5px] tracking-wider uppercase text-muted hover:text-text border border-border2 hover:border-wb rounded px-2 py-1"
              >
                None
              </button>
              <span className="ml-auto text-[11px] text-muted self-center">
                {ids.length}/{ALL_IDS.length} selected
              </span>
            </div>
            <ul className="space-y-1.5 max-h-[360px] overflow-y-auto pr-1">
              {ALL_IDS.map((id) => (
                <li key={id}>
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={selected.has(id)}
                      onChange={() => toggle(id)}
                      className="accent-wb"
                    />
                    <span className="text-[13px] text-text">{PARAMETERS[id].displayName}</span>
                    <span className="text-[11px] text-dim mono-nums ml-auto">
                      {PARAMETERS[id].unit}
                    </span>
                  </label>
                </li>
              ))}
            </ul>
          </Section>

          <Section
            title="Slurry monitoring"
            subtitle="Slurry PC log for the time range above — separate CSV file, one column per variable, timestamps in UTC."
          >
            <div className="text-[11.5px] text-muted mono-nums">
              {slurry.isLoading
                ? "Loading…"
                : slurry.isError
                  ? <span className="text-wb">Couldn't load slurry status.</span>
                  : slurryCount === 0
                    ? "No slurry data received yet."
                    : `${slurryCount.toLocaleString()} readings · ${slurry.data!.variables.length} variables · latest ${slurryLast!.replace("T", " ").slice(0, 19)} UTC`}
            </div>
            <a
              href={slurryHref ?? undefined}
              onClick={() => trackFeature("csv_export_slurry")}
              className={clsx(
                "block w-full rounded px-3 py-2 text-[12px] uppercase tracking-widest text-center border",
                slurryHref && slurryCount > 0
                  ? "border-border2 hover:border-wb hover:text-wb text-muted"
                  : "border-border1 text-dim pointer-events-none",
              )}
            >
              Download slurry CSV
            </a>
          </Section>

          <Section
            title="Diary"
            subtitle="Operator log entries — separate CSV file."
          >
            <a
              href="/api/diary/export.csv"
              className="block w-full rounded px-3 py-2 text-[12px] uppercase tracking-widest text-center border border-border2 hover:border-wb hover:text-wb text-muted"
            >
              Download diary CSV
            </a>
          </Section>

          {error && (
            <div className="text-[12px] text-wb bg-white border border-wb/40 rounded p-2">
              {error}
            </div>
          )}
        </div>

        <div className="absolute bottom-0 inset-x-0 p-4 border-t border-border1 bg-surface1">
          <button
            disabled={!canDownload}
            onClick={handleDownload}
            className={clsx(
              "w-full rounded px-3 py-2.5 text-[12px] uppercase tracking-widest transition-colors",
              canDownload
                ? "bg-wb hover:bg-wbHot text-white"
                : "bg-border1 text-dim cursor-not-allowed",
            )}
          >
            {downloading ? "Preparing…" : `Download ${mode === "raw" ? "raw" : "processed"} CSV`}
          </button>
        </div>
      </aside>
    </>
  );
}

function Section({
  title,
  subtitle,
  children,
}: {
  title: string;
  subtitle?: string;
  children: React.ReactNode;
}) {
  return (
    <section>
      <h3 className="text-[14px] font-semibold tracking-[0.14em] uppercase text-text">{title}</h3>
      {subtitle && <p className="text-[11.5px] text-muted mt-1 mb-3">{subtitle}</p>}
      {!subtitle && <div className="mb-3" />}
      <div className="space-y-3">{children}</div>
    </section>
  );
}

function DateField({
  label,
  value,
  onChange,
}: {
  label: string;
  value: string;
  onChange: (v: string) => void;
}) {
  return (
    <label className="block">
      <div className="text-[11px] text-muted mb-1.5">{label}</div>
      <input
        type="datetime-local"
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className="w-full bg-surface2 border border-border1 rounded px-3 py-2 text-text text-[13px] mono-nums focus:border-wb/60 outline-none"
      />
    </label>
  );
}
